/**
 * /services — public browse page for Memorial Services requests.
 * Compelling landing hero on top, then category chips + search + card grid.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import {
  Heart, MapPin, Sparkles, Search, ChevronRight, Home, Flower, HandHeart,
  Landmark, Users, BookHeart, MessageCircle, Star, Church,
} from "lucide-react";

const CATEGORY_ICONS = {
  "Memorial & Grave Care": Flower,
  "Family Home & Heritage": Home,
  "Family & Friends": Users,
  "Acts of Kindness": HandHeart,
  "Religious & Cultural Services": Church,
  "Heritage Preservation": BookHeart,
  "Community Connections": Landmark,
  "Special Requests": Sparkles,
  "Premium Services": Star,
};

export default function Services() {
  const [taxonomy, setTaxonomy] = useState({});
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    http.get("/services/config").then((r) => setTaxonomy(r.data.taxonomy || {})).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    http.get("/services", { params: { category: category || undefined, q: q || undefined, limit: 60 } })
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => toast.error("Failed to load requests"))
      .finally(() => setLoading(false));
  }, [category, q]);

  const categories = Object.keys(taxonomy);

  return (
    <div data-testid="services-page">
      {/* Hero */}
      <section
        className="border-b py-16 md:py-20"
        style={{ background: "var(--wofy-surface)", borderColor: "var(--wofy-border)" }}
      >
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <div className="overline mb-3">CELEBRATE A LIFE · SERVICES</div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-tight max-w-3xl">
            Request a Service to Cherish Memories.
          </h1>
          <p className="mt-5 text-base md:text-lg max-w-3xl leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
            Distance should never keep you from honoring the people and places that shaped
            your life. Home is more than a place — it is where our memories, loved ones,
            and family stories live. As families spread across the world, visiting a
            parent's or loved one's grave, checking on an ancestral home, embracing an
            elderly relative, or helping someone in your hometown is not always possible.
          </p>
          <p className="mt-4 text-base md:text-lg max-w-3xl leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
            <strong>Celebrate a Life</strong> bridges that distance by connecting you with
            trusted local people who can carry out these meaningful acts on your behalf
            with care, respect, and compassion. Every completed service includes photos,
            videos, or live updates.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/services/submit" data-testid="services-submit-cta" className="wofy-btn-primary">
              <Heart className="w-4 h-4" /> Request a service
            </Link>
            <a href="#browse" className="wofy-btn-ghost">
              Browse recent requests <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Categories band */}
      <section className="py-14 border-b" style={{ borderColor: "var(--wofy-border)" }}>
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <div className="overline mb-3">WHAT WE CAN HELP WITH</div>
          <h2 className="font-display text-3xl mb-8">Nine categories of care.</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {categories.map((cat) => {
              const Icon = CATEGORY_ICONS[cat] || Sparkles;
              return (
                <button
                  key={cat}
                  onClick={() => { setCategory(cat === category ? "" : cat); document.getElementById("browse")?.scrollIntoView({ behavior: "smooth" }); }}
                  data-testid={`services-cat-${cat.replace(/\W+/g, "-").toLowerCase()}`}
                  className="text-left rounded-xl p-5 border transition-all hover:shadow-sm"
                  style={{
                    borderColor: category === cat ? "var(--wofy-primary)" : "var(--wofy-border)",
                    background: category === cat ? "var(--wofy-surface)" : "transparent",
                  }}
                >
                  <div
                    className="w-10 h-10 rounded-full grid place-items-center mb-3"
                    style={{ background: "var(--wofy-surface)", color: "var(--wofy-primary)" }}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="font-display text-lg leading-tight">{cat}</div>
                  <div className="mt-1 text-xs opacity-70">
                    {(taxonomy[cat] || []).length} services available
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Browse */}
      <section id="browse" className="py-14">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="overline mb-2">RECENT REQUESTS</div>
              <h2 className="font-display text-3xl">
                {category ? category : "Requests from the community"}
                <span className="text-base opacity-60 font-normal ml-2">({total})</span>
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-4 h-4 absolute top-1/2 -translate-y-1/2 left-3 opacity-50 pointer-events-none" />
                <input
                  data-testid="services-search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search…"
                  className="pl-9 pr-3 py-2 rounded-full border bg-transparent text-sm"
                  style={{ borderColor: "var(--wofy-border)" }}
                />
              </div>
              {category && (
                <button
                  onClick={() => setCategory("")}
                  data-testid="services-clear-cat"
                  className="text-xs underline opacity-70 hover:opacity-100"
                >
                  Clear filter
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="mt-10 text-sm opacity-60 text-center">Loading requests…</div>
          ) : items.length === 0 ? (
            <div className="mt-10 wofy-card text-center py-12">
              <Heart className="w-8 h-8 mx-auto opacity-40 mb-3" />
              <div className="font-display text-xl">Be the first to request a service.</div>
              <p className="text-xs opacity-70 mt-1 max-w-md mx-auto">
                {category
                  ? `No approved ${category} requests yet — check back soon, or add yours.`
                  : "The community is just getting started here. Share the memory you want honored."}
              </p>
              <Link to="/services/submit" className="wofy-btn-primary mt-5 inline-flex">
                <Heart className="w-4 h-4" /> Post a request
              </Link>
            </div>
          ) : (
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((s) => <ServiceCard key={s.id} s={s} />)}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function ServiceCard({ s }) {
  const Icon = CATEGORY_ICONS[s.category] || Sparkles;
  return (
    <Link
      to={`/services/${s.slug}`}
      data-testid={`service-card-${s.slug}`}
      className="wofy-card block hover:shadow-md transition-shadow"
    >
      {s.cover_image ? (
        <img src={s.cover_image} alt="" className="w-full h-40 object-cover rounded-lg mb-3" />
      ) : (
        <div className="w-full h-40 grid place-items-center rounded-lg mb-3" style={{ background: "var(--wofy-surface)" }}>
          <Icon className="w-8 h-8 opacity-40" />
        </div>
      )}
      <div className="overline text-[10px]">{s.category}</div>
      <div className="font-display text-lg mt-1 leading-tight line-clamp-2">{s.title}</div>
      <p className="mt-2 text-xs opacity-75 line-clamp-3">{s.short_description}</p>
      {(s.memorial_city || s.memorial_country) && (
        <div className="mt-3 text-[11px] font-mono opacity-60 inline-flex items-center gap-1">
          <MapPin className="w-3 h-3" />
          {[s.memorial_city, s.memorial_state, s.memorial_country].filter(Boolean).join(", ")}
        </div>
      )}
    </Link>
  );
}
